import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import fetchToBackend, { buildQuery } from "@/utils/fetchToBackend";
import { toContractingParams } from "@/lib/contracting-params";
import { contractingKeys } from "@/lib/query-keys";
import type {
  ContractingFilters,
  ContractingItem,
  ContractingListResponse,
} from "@/types/domain";

/** The backend wraps list responses in `{ data: { pagination, contractings } }`. */
interface RawListResponse {
  data?: ContractingListResponse;
  pagination?: ContractingListResponse["pagination"];
  contractings?: ContractingItem[];
}

/**
 * Listado de licitaciones. Sin `searchProfileId` mira el pipeline completo de la
 * empresa, que es lo que pide el tablero de seguimiento: el backend lo permite
 * mientras venga un filtro de estado.
 */
export function useContractings(filters: ContractingFilters, enabled = true) {
  return useQuery({
    queryKey: contractingKeys.list(filters),
    enabled:
      enabled && (filters.searchProfileId != null || !!filters.status?.length),
    // Keep the previous page/tab visible while the next one loads.
    placeholderData: keepPreviousData,
    // Un cruce en curso termina en segundos: se pregunta seguido hasta ver su veredicto.
    refetchInterval: (query) =>
      query.state.data?.contractings.some((c) => c.qualification?.status === "ANALYZING")
        ? 5000
        : false,
    queryFn: async () => {
      const query = buildQuery(toContractingParams(filters));
      const res = await fetchToBackend<RawListResponse>(
        `/contractings/getAll${query}`,
      );
      const data = res.data ?? res;
      return {
        contractings: data.contractings ?? [],
        pagination: data.pagination ?? {
          length: 0,
          size: filters.size ?? 10,
          page: filters.page ?? 0,
          lastPage: 0,
        },
      } as ContractingListResponse;
    },
  });
}

/**
 * Detalle de una licitación. `enabled: false` evita el request cuando el
 * llamador ya sabe que está bloqueada (el backend respondería 403).
 */
export function useContracting(
  id?: number,
  searchProfileId?: number,
  options?: { enabled?: boolean },
) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: contractingKeys.detail(id, searchProfileId),
    enabled: id != null && options?.enabled !== false,
    refetchInterval: (query) =>
      query.state.data?.qualificationState === "ANALYZING" ? 5000 : false,
    // Seed an instant preview from any list cache that already holds this row.
    placeholderData: () => {
      if (id == null) return undefined;
      const lists = queryClient.getQueriesData<ContractingListResponse>({
        queryKey: contractingKeys.lists(),
      });
      for (const [, value] of lists) {
        const found = value?.contractings?.find(
          (c) => c.contracting_id === id,
        );
        if (found) return found as any;
      }
      return undefined;
    },
    queryFn: async () => {
      const query = buildQuery({ searchProfileId });
      const res = await fetchToBackend<any>(
        `/contractings/getById/${id}${query}`,
      );
      return res?.data ?? res;
    },
  });
}
